import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import {
  getEmpresa, listRecordatorios, createRecordatorio, updateRecordatorio,
  deleteRecordatorio, getRecordatoriosMeta, getRecordatoriosTrabajadores, marcarRecordatorioRealizado,
} from "@/lib/prevelis";
import { formatFecha } from "@/lib/format";
import { toast } from "sonner";
import {
  Plus, ArrowLeft, User, Building2, Pencil, Trash2, X, Check, RotateCcw,
  CircleAlert, Clock, CircleCheck, CalendarOff, CheckCircle2,
} from "lucide-react";

const URGENCIA = {
  vencida: { label: "Vencida", cls: "bg-red-50 text-red-700 border-red-200", dot: "bg-red-500", Icon: CircleAlert },
  proxima: { label: "Próxima", cls: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500", Icon: Clock },
  en_plazo: { label: "En plazo", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500", Icon: CircleCheck },
  sin_fecha: { label: "Sin fecha", cls: "bg-slate-100 text-slate-500 border-slate-200", dot: "bg-slate-400", Icon: CalendarOff },
  realizada: { label: "Realizada", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500", Icon: CheckCircle2 },
};

const EMPTY = {
  tipo: "formacion", trabajador: "", descripcion: "",
  modo: "periodicidad", fecha_realizacion: "", periodicidad_meses: 12, fecha_caducidad: "", notas: "",
};

function diasTexto(d) {
  if (d == null) return "Sin fecha de caducidad";
  if (d < 0) return `Venció hace ${Math.abs(d)} día${Math.abs(d) === 1 ? "" : "s"}`;
  if (d === 0) return "Vence hoy";
  return `Vence en ${d} día${d === 1 ? "" : "s"}`;
}

const field = "w-full h-11 px-3 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none";

export default function RecordatoriosEmpresa() {
  const { empresaId } = useParams();
  const navigate = useNavigate();
  const [empresa, setEmpresa] = useState(null);
  const [items, setItems] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [trabajadores, setTrabajadores] = useState([]);
  const [dialog, setDialog] = useState(null); // {editId?, form}

  const load = useCallback(() => {
    listRecordatorios(empresaId).then(setItems).catch(() => {});
  }, [empresaId]);

  useEffect(() => {
    getEmpresa(empresaId).then(setEmpresa).catch(() => {});
    getRecordatoriosMeta().then((m) => setTipos(m.tipos || [])).catch(() => {});
    getRecordatoriosTrabajadores(empresaId).then(setTrabajadores).catch(() => {});
    load();
  }, [empresaId, load]);

  const openNew = () => setDialog({ editId: null, form: { ...EMPTY } });
  const openEdit = (r) => setDialog({
    editId: r.id,
    form: {
      tipo: r.tipo, trabajador: r.trabajador || "", descripcion: r.descripcion || "",
      modo: r.fecha_caducidad ? "fecha" : "periodicidad",
      fecha_realizacion: r.fecha_realizacion || "",
      periodicidad_meses: r.periodicidad_meses || 12,
      fecha_caducidad: r.fecha_caducidad || "", notas: r.notas || "",
    },
  });

  const setF = (k, v) => setDialog((d) => ({ ...d, form: { ...d.form, [k]: v } }));

  const save = async () => {
    const f = dialog.form;
    const payload = {
      empresa_id: empresaId, tipo: f.tipo, trabajador: f.trabajador, descripcion: f.descripcion, notas: f.notas,
    };
    if (f.modo === "fecha") {
      payload.fecha_caducidad = f.fecha_caducidad;
      payload.fecha_realizacion = "";
      payload.periodicidad_meses = null;
    } else {
      payload.fecha_realizacion = f.fecha_realizacion;
      payload.periodicidad_meses = f.periodicidad_meses ? Number(f.periodicidad_meses) : null;
      payload.fecha_caducidad = "";
    }
    try {
      if (dialog.editId) await updateRecordatorio(dialog.editId, payload);
      else await createRecordatorio(payload);
      toast.success(dialog.editId ? "Recordatorio actualizado" : "Recordatorio añadido");
      setDialog(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    }
  };

  const remove = async (r) => {
    if (!window.confirm("¿Eliminar este recordatorio?")) return;
    await deleteRecordatorio(r.id);
    toast.success("Recordatorio eliminado");
    load();
  };

  const toggleRealizado = async (r) => {
    try {
      const res = await marcarRecordatorioRealizado(r.id, !r.realizado);
      if (res?.reprogramado_nuevo) toast.success("Realizada · siguiente caducidad reprogramada automáticamente");
      else toast.success(r.realizado ? "Marcado como pendiente" : "Marcado como realizada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar");
    }
  };

  // Agrupar por trabajador (los sin trabajador → "General de la empresa")
  const grupos = {};
  items.forEach((r) => {
    const key = (r.trabajador || "").trim() || "__general__";
    (grupos[key] = grupos[key] || []).push(r);
  });
  const ordenU = { vencida: 0, proxima: 1, en_plazo: 2, sin_fecha: 3, realizada: 4 };
  Object.values(grupos).forEach((arr) => arr.sort((a, b) => ordenU[a.urgencia] - ordenU[b.urgencia]));
  const claves = Object.keys(grupos).sort((a, b) =>
    a === "__general__" ? -1 : b === "__general__" ? 1 : a.localeCompare(b)
  );

  return (
    <Layout
      title={empresa?.razon_social || "Recordatorios"}
      subtitle="Vencimientos por trabajador · formación, EPIs, reconocimientos y revisiones"
      actions={
        <button onClick={openNew} data-testid="rec-nuevo" className="inline-flex items-center gap-2 h-11 px-4 rounded-lg bg-blue-700 text-white font-medium hover:bg-blue-800">
          <Plus className="w-5 h-5" /> <span className="hidden sm:inline">Añadir recordatorio</span>
        </button>
      }
    >
      <button onClick={() => navigate("/recordatorios")} className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 hover:text-blue-800 mb-5">
        <ArrowLeft className="w-4 h-4" /> Volver a empresas
      </button>

      {items.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center text-slate-500" data-testid="rec-vacio">
          Sin recordatorios. Pulsa <span className="font-semibold text-slate-700">Añadir recordatorio</span> para registrar el primero.
        </div>
      ) : (
        <div className="space-y-8" data-testid="rec-lista">
          {claves.map((k) => (
            <div key={k}>
              <div className="flex items-center gap-2 mb-3">
                {k === "__general__" ? <Building2 className="w-4 h-4 text-slate-500" /> : <User className="w-4 h-4 text-slate-500" />}
                <h3 className="font-heading font-semibold text-slate-900">{k === "__general__" ? "General de la empresa" : k}</h3>
                <span className="text-xs text-slate-400">({grupos[k].length})</span>
              </div>
              <div className="space-y-3">
                {grupos[k].map((r) => {
                  const u = URGENCIA[r.urgencia] || URGENCIA.sin_fecha;
                  return (
                    <div key={r.id} data-testid={`rec-item-${r.id}`} className={`border rounded-xl p-4 flex items-center gap-3 ${r.realizado ? "bg-slate-50 border-slate-200 opacity-80" : "bg-white border-slate-200"}`}>
                      <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${u.dot}`} />
                      <div className="min-w-0 flex-1">
                        <div className={`font-medium text-slate-900 ${r.realizado ? "line-through text-slate-500" : ""}`}>{r.tipo_label}{r.descripcion ? ` — ${r.descripcion}` : ""}</div>
                        <div className="text-xs text-slate-400 mt-0.5">
                          {r.realizado ? `Realizada${r.realizado_at ? ` · ${formatFecha(r.realizado_at)}` : ""}` : `${diasTexto(r.dias_restantes)}${r.caducidad ? ` · ${formatFecha(r.caducidad)}` : ""}`}
                        </div>
                        {r.notas && <div className="text-xs text-slate-500 mt-1 truncate">{r.notas}</div>}
                      </div>
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full border inline-flex items-center gap-1 shrink-0 ${u.cls}`}>
                        <u.Icon className="w-3.5 h-3.5" /> {u.label}
                      </span>
                      <button
                        onClick={() => toggleRealizado(r)}
                        data-testid={`rec-realizado-${r.id}`}
                        title={r.realizado ? "Marcar como pendiente" : "Marcar como realizada"}
                        className={`inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg text-sm font-medium border ${r.realizado ? "border-slate-300 text-slate-600 hover:bg-slate-100" : "border-emerald-600 text-emerald-700 hover:bg-emerald-50"}`}
                      >
                        {r.realizado ? <><RotateCcw className="w-4 h-4" /> Reabrir</> : <><Check className="w-4 h-4" /> Realizada</>}
                      </button>
                      <button onClick={() => openEdit(r)} data-testid={`rec-edit-${r.id}`} className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-600">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => remove(r)} data-testid={`rec-del-${r.id}`} className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {dialog && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setDialog(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="rec-dialog">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-lg text-slate-900">{dialog.editId ? "Editar recordatorio" : "Nuevo recordatorio"}</h3>
              <button onClick={() => setDialog(null)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Tipo</label>
                <select data-testid="rec-form-tipo" value={dialog.form.tipo} onChange={(e) => setF("tipo", e.target.value)} className={field + " mt-1"}>
                  {tipos.map((t) => <option key={t.codigo} value={t.codigo}>{t.etiqueta}</option>)}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Trabajador <span className="text-slate-400 font-normal">(opcional)</span></label>
                <input data-testid="rec-form-trabajador" list="rec-trabajadores" value={dialog.form.trabajador} onChange={(e) => setF("trabajador", e.target.value)} placeholder="Deja vacío para un recordatorio general de la empresa" className={field + " mt-1"} />
                <datalist id="rec-trabajadores">
                  {trabajadores.map((t) => <option key={t} value={t} />)}
                </datalist>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Descripción <span className="text-slate-400 font-normal">(opcional)</span></label>
                <input data-testid="rec-form-descripcion" value={dialog.form.descripcion} onChange={(e) => setF("descripcion", e.target.value)} placeholder="Ej. Formación PRL nivel básico, EPI guantes anticorte…" className={field + " mt-1"} />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 block mb-2">Caducidad</label>
                <div className="flex gap-2 mb-3">
                  <button type="button" data-testid="rec-modo-periodicidad" onClick={() => setF("modo", "periodicidad")} className={`h-10 px-3 rounded-lg text-sm font-medium border ${dialog.form.modo === "periodicidad" ? "bg-blue-700 text-white border-blue-700" : "bg-white text-slate-600 border-slate-300"}`}>
                    Realización + periodicidad
                  </button>
                  <button type="button" data-testid="rec-modo-fecha" onClick={() => setF("modo", "fecha")} className={`h-10 px-3 rounded-lg text-sm font-medium border ${dialog.form.modo === "fecha" ? "bg-blue-700 text-white border-blue-700" : "bg-white text-slate-600 border-slate-300"}`}>
                    Fecha de caducidad
                  </button>
                </div>
                {dialog.form.modo === "periodicidad" ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-slate-500">Fecha de realización</label>
                      <input type="date" data-testid="rec-form-realizacion" value={dialog.form.fecha_realizacion} onChange={(e) => setF("fecha_realizacion", e.target.value)} className={field + " mt-1"} />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500">Periodicidad (meses)</label>
                      <input type="number" min="1" data-testid="rec-form-periodicidad" value={dialog.form.periodicidad_meses} onChange={(e) => setF("periodicidad_meses", e.target.value)} className={field + " mt-1"} />
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="text-xs text-slate-500">Fecha de caducidad</label>
                    <input type="date" data-testid="rec-form-caducidad" value={dialog.form.fecha_caducidad} onChange={(e) => setF("fecha_caducidad", e.target.value)} className={field + " mt-1"} />
                  </div>
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Notas <span className="text-slate-400 font-normal">(opcional)</span></label>
                <textarea data-testid="rec-form-notas" value={dialog.form.notas} onChange={(e) => setF("notas", e.target.value)} rows={2} className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none mt-1" />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setDialog(null)} className="h-11 px-4 rounded-lg border border-slate-300 font-medium hover:bg-slate-50">Cancelar</button>
              <button onClick={save} data-testid="rec-form-guardar" className="h-11 px-5 rounded-lg bg-blue-700 text-white font-medium hover:bg-blue-800">Guardar</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
